
import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Brain, Sparkles, ChevronDown, Lightbulb, DollarSign, Target, BookOpen, FileText, Building, Phone, Scale } from 'lucide-react';
import { navigationItems, aiFeatures, publicNavigationItems } from './navigationData';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

interface DesktopNavigationProps {
  user: any;
  isActive: (path: string) => boolean;
}

const DesktopNavigation = ({ user, isActive }: DesktopNavigationProps) => {
  const [isAIMenuOpen, setIsAIMenuOpen] = useState(false);
  const [isProductMenuOpen, setIsProductMenuOpen] = useState(false);
  const [isResourcesMenuOpen, setIsResourcesMenuOpen] = useState(false);
  const aiMenuRef = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();

  const productItems = [
    { path: '/features', label: t('nav.features'), icon: Lightbulb, description: t('nav.discoverFeatures') },
    ...(user ? [] : [{ path: '/product/ai-predictive', label: t('nav.aiPredictive'), icon: Brain, description: t('nav.intelligentPredictions') }]),
    { path: '/pricing', label: t('nav.pricing'), icon: DollarSign, description: 'Planes y precios transparentes' },
    { path: '/use-cases', label: t('nav.useCases'), icon: Target, description: t('nav.realUseCases') }
  ];

  const resourceItems = [
    { path: '/blog', label: t('nav.blog'), icon: FileText, description: t('nav.guidesAndPractices') },
    { path: '/comparativas', label: t('nav.comparisons'), icon: Scale, description: t('nav.compareSoftware') },
    { path: '/user-guides', label: t('nav.userGuides'), icon: BookOpen, description: t('nav.learnStepByStep') }
  ];

  const companyItems = [
    { path: '/about-us', label: t('nav.aboutUs'), icon: Building, description: t('nav.knowOurTeam') },
    { path: '/contact', label: t('nav.contact'), icon: Phone, description: t('nav.getInTouch') }
  ];

  return (
    <nav className="hidden md:flex items-center space-x-1">
      {user ? (
        <>
          {navigationItems.map(({ path, labelKey, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              className={cn(
                "group relative flex items-center space-x-2 px-4 py-2.5 rounded-2xl text-sm font-medium transition-all duration-500 overflow-hidden",
                isActive(path)
                  ? "bg-gradient-to-r from-primary/30 via-primary/20 to-primary/10 text-primary shadow-xl scale-105 border border-primary/20"
                  : "text-muted-foreground hover:text-primary hover:bg-gradient-to-r hover:from-primary/15 hover:via-primary/10 hover:to-primary/5 hover:shadow-lg hover:scale-105 hover:border hover:border-primary/10"
              )}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className={cn(
                "p-1.5 rounded-lg transition-all duration-300 group-hover:scale-110",
                isActive(path) ? "bg-primary/20 shadow-md" : "group-hover:bg-primary/10"
              )}>
                <Icon className={cn(
                  "h-4 w-4 relative z-10 transition-all duration-300",
                  isActive(path) ? "text-primary" : "group-hover:text-primary"
                )} />
              </div>
              <span className="relative z-10 font-semibold">{labelKey ? t(labelKey) : ''}</span>
              {isActive(path) && (
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-gradient-to-r from-primary via-primary/80 to-primary rounded-full animate-pulse" />
              )}
              <div className="absolute top-1 right-1 w-2 h-2 bg-gradient-to-r from-primary/40 to-info/40 rounded-full opacity-0 group-hover:opacity-100 animate-ping" />
            </Link>
          ))}
          
          {/* AI Features Dropdown */}
          <div className="relative" ref={aiMenuRef}>
            <Button
              variant="ghost"
              className={cn(
                "group relative flex items-center space-x-2 px-4 py-2.5 rounded-2xl transition-all duration-500 overflow-hidden",
                aiFeatures.some(item => isActive(item.path))
                  ? "bg-gradient-to-r from-status-purple/30 via-info/20 to-status-purple/10 text-status-purple shadow-xl scale-105 border border-status-purple/20"
                  : "text-muted-foreground hover:text-status-purple hover:bg-gradient-to-r hover:from-status-purple/15 hover:via-info/10 hover:to-status-purple/5 hover:shadow-lg hover:scale-105 hover:border hover:border-status-purple/10"
              )}
              onClick={() => setIsAIMenuOpen(!isAIMenuOpen)}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-status-purple/5 via-info/5 to-status-purple/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="p-1.5 rounded-lg bg-gradient-to-br from-status-purple-muted to-info-muted group-hover:scale-110 transition-transform duration-300 shadow-sm">
                <Brain className="h-4 w-4 relative z-10 text-status-purple transition-transform duration-300 group-hover:rotate-12" />
              </div>
              <span className="relative z-10 font-semibold">{t('nav.aiAdvanced')}</span>
              <ChevronDown className={cn(
                "h-3 w-3 relative z-10 transition-all duration-500",
                isAIMenuOpen ? "rotate-180 scale-110" : "group-hover:rotate-180 group-hover:scale-110"
              )} />
              <Sparkles className="absolute top-0.5 right-0.5 h-3 w-3 text-status-purple/75 animate-pulse" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-status-purple to-info rounded-full opacity-0 group-hover:opacity-100 animate-ping" />
            </Button>
            
            {isAIMenuOpen && (
              <div className="absolute right-0 mt-3 w-80 bg-popover/95 backdrop-blur-xl rounded-3xl shadow-2xl ring-1 ring-border/20 z-50 border border-status-purple-border/50 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-status-purple-muted/70 via-info-muted/70 to-status-purple-muted/70" />
                <div className="relative p-3">
                  <div className="text-center py-2 border-b border-status-purple-border/50 mb-2">
                    <h3 className="text-sm font-bold text-status-purple flex items-center justify-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      {t('nav.artificialIntelligence')}
                      <Sparkles className="h-4 w-4" />
                    </h3>
                  </div>
                  {aiFeatures.map(({ path, labelKey, descriptionKey, icon: Icon }) => (
                    <Link
                      key={path}
                      to={path}
                      className={cn(
                        "flex items-center space-x-3 px-4 py-3 mb-2 rounded-2xl text-sm transition-all duration-300 relative group",
                        isActive(path)
                          ? "bg-gradient-to-r from-status-purple-muted to-info-muted text-status-purple shadow-lg scale-105"
                          : "text-foreground hover:bg-gradient-to-r hover:from-status-purple-muted/50 hover:to-info-muted/50 hover:text-status-purple hover:shadow-md hover:scale-105"
                      )}
                      onClick={() => setIsAIMenuOpen(false)}
                    >
                      <div className="p-2 rounded-xl bg-gradient-to-br from-status-purple-muted to-info-muted group-hover:scale-110 transition-transform duration-300 shadow-sm">
                        <Icon className="h-4 w-4 text-status-purple" />
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold block">{labelKey ? t(labelKey) : ''}</span>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {descriptionKey ? t(descriptionKey) : ''}
                        </div>
                      </div>
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-status-purple to-info opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-pulse" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Product Dropdown */}
          <div className="relative">
            <Button
              variant="ghost"
              className="group relative flex items-center space-x-2 px-4 py-2.5 rounded-2xl transition-all duration-500 text-muted-foreground hover:text-primary hover:bg-gradient-to-r hover:from-primary/15 hover:to-primary/5 hover:shadow-lg hover:scale-105"
              onClick={() => {setIsProductMenuOpen(!isProductMenuOpen); setIsResourcesMenuOpen(false)}}
            >
              <Lightbulb className="h-4 w-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" />
              <span className="font-semibold">{t('nav.product')}</span>
              <ChevronDown className={cn("h-3 w-3 transition-all duration-300", isProductMenuOpen && "rotate-180")} />
            </Button>
            
            {isProductMenuOpen && (
              <div className="absolute left-0 mt-3 w-72 bg-popover/95 backdrop-blur-xl rounded-3xl shadow-2xl ring-1 ring-border/20 z-50 border border-primary/10 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10" />
                <div className="relative p-3">
                  {productItems.map(({ path, label, icon: Icon, description }) => (
                    <Link
                      key={path}
                      to={path}
                      className="flex items-center space-x-3 px-4 py-3 mb-2 rounded-2xl text-sm transition-all duration-300 hover:bg-gradient-to-r hover:from-primary/10 hover:to-primary/5 hover:shadow-md hover:scale-105 group"
                      onClick={() => setIsProductMenuOpen(false)}
                    >
                      <div className="p-2 rounded-xl bg-gradient-to-br from-primary/10 to-primary/20 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold block text-foreground">{label}</span>
                        <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Resources Dropdown */}
          <div className="relative">
            <Button
              variant="ghost"
              className="group relative flex items-center space-x-2 px-4 py-2.5 rounded-2xl transition-all duration-500 text-muted-foreground hover:text-info hover:bg-gradient-to-r hover:from-info/15 hover:to-info/5 hover:shadow-lg hover:scale-105"
              onClick={() => {setIsResourcesMenuOpen(!isResourcesMenuOpen); setIsProductMenuOpen(false)}}
            >
              <BookOpen className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
              <span className="font-semibold">{t('nav.resources')}</span>
              <ChevronDown className={cn("h-3 w-3 transition-all duration-300", isResourcesMenuOpen && "rotate-180")} />
            </Button>
            
            {isResourcesMenuOpen && (
              <div className="absolute left-0 mt-3 w-72 bg-popover/95 backdrop-blur-xl rounded-3xl shadow-2xl ring-1 ring-border/20 z-50 border border-info-border/30 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-info-muted/70 to-info-muted/30" />
                <div className="relative p-3">
                  {resourceItems.map(({ path, label, icon: Icon, description }) => (
                    <Link
                      key={path}
                      to={path}
                      className="flex items-center space-x-3 px-4 py-3 mb-2 rounded-2xl text-sm transition-all duration-300 hover:bg-gradient-to-r hover:from-info/10 hover:to-info/5 hover:shadow-md hover:scale-105 group"
                      onClick={() => setIsResourcesMenuOpen(false)}
                    >
                      <div className="p-2 rounded-xl bg-gradient-to-br from-info-muted to-info-muted/60 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="h-4 w-4 text-info" />
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold block text-foreground">{label}</span>
                        <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Company links */}
          {companyItems.map(({ path, label, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              className={cn(
                "group relative flex items-center space-x-2 px-4 py-2.5 rounded-2xl text-sm font-medium transition-all duration-500 overflow-hidden",
                isActive(path)
                  ? "bg-gradient-to-r from-muted to-muted/60 text-foreground shadow-lg scale-105"
                  : "text-muted-foreground hover:text-foreground hover:bg-gradient-to-r hover:from-muted/50 hover:to-muted/30 hover:shadow-md hover:scale-105"
              )}
            >
              <div className="p-1.5 rounded-lg transition-all duration-300 group-hover:scale-110 group-hover:bg-muted">
                <Icon className="h-4 w-4 relative z-10 transition-transform duration-300" />
              </div>
              <span className="relative z-10 font-semibold">{label}</span>
            </Link>
          ))}

          {/* Integrations */}
          <Link
            to="/integrations"
            className={cn(
              "group relative flex items-center space-x-2 px-4 py-2.5 rounded-2xl text-sm font-medium transition-all duration-500 overflow-hidden",
              isActive('/integrations')
                ? "bg-gradient-to-r from-success/20 to-success/10 text-success shadow-lg scale-105 border border-success/20"
                : "text-muted-foreground hover:text-success hover:bg-gradient-to-r hover:from-success/10 hover:to-success/5 hover:shadow-md hover:scale-105"
            )}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-success/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="p-1.5 rounded-lg bg-gradient-to-br from-success-muted to-success-muted/70 transition-all duration-300 group-hover:scale-110">
              <div className="h-4 w-4 relative z-10 bg-gradient-to-r from-success to-success/80 rounded-sm flex items-center justify-center">
                <div className="w-2 h-2 bg-success-foreground rounded-full"></div>
              </div>
            </div>
            <span className="relative z-10 font-semibold">{t('nav.integrations')}</span>
            {isActive('/integrations') && (
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-1 bg-gradient-to-r from-success to-success/80 rounded-full animate-pulse" />
            )}
          </Link>
        </>
      )}
    </nav>
  );
};

export default DesktopNavigation;
